import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowForwardIcon,
  Button,
  CheckCircleIcon,
  CheckCircleOutlineIcon,
  RocketLaunchIcon,
  Text,
} from '@billda/design-system';
import { useNavigate, useLocation } from 'react-router-dom';
import { Permissions, usePermission } from '@billda/permissions-ui';
import { Routes } from '../../../routes';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import { useCompleteOnboarding } from '../hooks/useCompleteOnboarding';
import { useAuthStore } from '../../auth/stores/authStore';
import { useShellStore } from '../../../shared/stores/shellStore';
import {
  TOTAL_STEPS,
  getCompletedCount,
  getNextStep,
  getLastCompletedStep,
} from '../utils/onboardingSteps';
import type { OnboardingStepKey } from '../utils/onboardingSteps';

const SUCCESS_INTERSTITIAL_MS = 2200;

const FAB_DESCRIPTIONS: Record<OnboardingStepKey, string> = {
  invite: 'Add workers so they can book work and get paid.',
  'import-site': 'Import a construction project to start tracking work.',
  'assign-sites': 'Control which workers can see and book work on each site.',
  'labour-groups': 'Organise trade categories so workers see the right items.',
  'assign-groups': 'Link each worker to their trade group.',
};

const EXPANDED_CONTENT_HEIGHT = 88;

/** Local animations — token-driven, no sx. */
const FAB_KEYFRAMES = `
@keyframes gsfab-pulse {
  0% { transform: scale(1); }
  40% { transform: scale(1.18); }
  100% { transform: scale(1); }
}
@keyframes gsfab-fade-in {
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes gsfab-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes gsfab-glow-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
}
@keyframes gsfab-scale-in {
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes gsfab-content-fade {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
`;

interface ProgressRingProps {
  completedSteps: number;
  totalSteps: number;
  allComplete: boolean;
  accentColor: string;
  isPulsing: boolean;
  size: number;
}

const ProgressRing: React.FC<ProgressRingProps> = ({
  completedSteps,
  totalSteps,
  allComplete,
  accentColor,
  isPulsing,
  size,
}) => {
  const progressValue = (completedSteps / totalSteps) * 100;
  const stroke = 3;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progressValue / 100);

  return (
    <div
      className={`relative inline-flex shrink-0 ${
        isPulsing ? 'animate-[gsfab-pulse_0.6s_ease]' : ''
      }`}
      data-testid="getting-started-fab-progress"
    >
      <div
        className="pointer-events-none absolute -inset-1.5 rounded-full"
        style={{
          background: `radial-gradient(circle, color-mix(in srgb, ${accentColor} 12%, transparent) 0%, transparent 70%)`,
        }}
      />
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={totalSteps}
        aria-valuenow={completedSteps}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          className="stroke-border-control"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          stroke={accentColor}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: 'stroke-dashoffset 0.3s ease',
            filter: `drop-shadow(0 0 4px color-mix(in srgb, ${accentColor} 40%, transparent))`,
          }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <Text
          as="span"
          className={`text-[0.72rem] font-extrabold tracking-tight ${
            allComplete ? 'text-content-success' : 'text-content-primary'
          }`}
        >
          {completedSteps}/{totalSteps}
        </Text>
      </div>
    </div>
  );
};

export const GettingStartedFab: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();
  const hasOrgManage = usePermission(Permissions.Organisation.Manage);
  const { data: status } = useOnboardingStatus();
  const { mutate: complete } = useCompleteOnboarding();

  const sidebarCollapsed = useShellStore(state => state.sidebarCollapsed);

  const [isPulsing, setIsPulsing] = useState(false);
  const [showingSuccess, setShowingSuccess] = useState(false);
  const [completedStepName, setCompletedStepName] = useState('');
  const prevCompletedRef = useRef<number | null>(null);
  const successTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const completedSteps = status ? getCompletedCount(status) : 0;
  const totalSteps = TOTAL_STEPS;
  const allComplete = completedSteps === totalSteps;
  const nextStep = status ? getNextStep(status) : null;

  // Detect step completion → show success interstitial
  useEffect(() => {
    if (
      prevCompletedRef.current !== null &&
      completedSteps > prevCompletedRef.current
    ) {
      const justCompleted = status ? getLastCompletedStep(status) : null;
      setCompletedStepName(justCompleted?.title ?? 'Step');
      setShowingSuccess(true);
      setIsPulsing(true);

      const pulseTimer = setTimeout(() => setIsPulsing(false), 600);
      successTimer.current = setTimeout(() => {
        setShowingSuccess(false);
      }, SUCCESS_INTERSTITIAL_MS);

      prevCompletedRef.current = completedSteps;
      return () => {
        clearTimeout(pulseTimer);
        clearTimeout(successTimer.current);
      };
    }
    prevCompletedRef.current = completedSteps;
  }, [completedSteps, status]);

  // Clean up success timer on unmount
  useEffect(() => {
    return () => clearTimeout(successTimer.current);
  }, []);

  const handleCtaClick = useCallback(() => {
    if (allComplete) {
      complete(undefined, {
        onSuccess: () => navigate(Routes.sites.manage, { replace: true }),
      });
    } else if (nextStep) {
      navigate(nextStep.ctaPath);
    }
  }, [allComplete, complete, navigate, nextStep]);

  // Visibility checks
  const isOnGettingStartedPage =
    location.pathname === Routes.onboarding.gettingStarted;
  const isInvitationFlow = location.pathname.startsWith('/invitation/');
  const isDownloadAppPage = location.pathname === Routes.downloadApp;
  const isOnboardingIncomplete = status && !status.gettingStartedCompletedAt;

  if (
    !isAuthenticated ||
    isInvitationFlow ||
    isDownloadAppPage ||
    !status ||
    !isOnboardingIncomplete ||
    !hasOrgManage ||
    isOnGettingStartedPage
  ) {
    return null;
  }

  const accentColor =
    allComplete || showingSuccess
      ? 'var(--color-content-success)'
      : 'var(--color-content-accent)';

  return (
    <>
      <style>{FAB_KEYFRAMES}</style>
      <div
        data-testid="getting-started-fab"
        className={`fixed bottom-16 left-1/2 z-[1050] -translate-x-1/2 animate-[gsfab-fade-in_0.5s_cubic-bezier(0.16,1,0.3,1)_both] transition-[left] duration-300 ease-in-out md:left-0 md:translate-x-0 ${
          sidebarCollapsed ? 'md:left-8' : 'md:left-[272px]'
        }`}
      >
        <div
          className="pointer-events-none absolute -inset-1 rounded-[20px] animate-[gsfab-glow-pulse_3s_ease-in-out_infinite]"
          style={{
            background: `radial-gradient(ellipse at center, color-mix(in srgb, ${accentColor} 13%, transparent) 0%, transparent 70%)`,
          }}
        />
        <div className="relative w-[340px] rounded-2xl border border-border-default bg-surface-raised px-5 py-4 shadow-popover backdrop-blur-xl">
          {/* Top row: content + progress ring */}
          <div className="flex gap-4">
            {/* Left: text content — fixed height */}
            <div
              className="flex min-w-0 flex-1 flex-col"
              style={{ height: EXPANDED_CONTENT_HEIGHT }}
            >
              {/* Success interstitial */}
              {showingSuccess && !allComplete ? (
                <div className="flex h-full animate-[gsfab-content-fade_0.3s_ease_both] flex-col justify-center gap-1.5">
                  <Text
                    as="span"
                    className="flex items-center gap-1 text-[0.6rem] font-bold tracking-[0.08em] text-content-success uppercase"
                  >
                    <CheckCircleIcon
                      aria-hidden="true"
                      className="size-[11px] animate-[gsfab-scale-in_0.4s_cubic-bezier(0.16,1,0.3,1)_both]"
                    />
                    Step complete
                  </Text>
                  <Text
                    as="span"
                    className="animate-[gsfab-content-fade_0.3s_ease_0.2s_both] text-[0.9rem] leading-tight font-bold tracking-tight text-content-success"
                  >
                    {completedStepName} — done!
                  </Text>
                  <Text
                    as="span"
                    variant="caption"
                    color="secondary"
                    className="animate-[gsfab-content-fade_0.3s_ease_0.35s_both] leading-normal"
                  >
                    {completedSteps}/{totalSteps} steps complete
                  </Text>
                </div>
              ) : (
                /* Normal step content */
                <div className="flex animate-[gsfab-content-fade_0.25s_ease_both] flex-col gap-1.5">
                  <Text
                    as="span"
                    className="flex items-center gap-1 text-[0.6rem] font-bold tracking-[0.08em] uppercase"
                    style={{ color: accentColor }}
                  >
                    <RocketLaunchIcon
                      aria-hidden="true"
                      className="size-[11px]"
                    />
                    {allComplete
                      ? 'Setup complete'
                      : `Step ${completedSteps + 1} of ${totalSteps}`}
                  </Text>

                  <Text
                    as="span"
                    className={`text-[0.9rem] leading-tight font-bold tracking-tight ${
                      allComplete
                        ? 'text-content-success'
                        : 'text-content-primary'
                    }`}
                  >
                    {allComplete ? "You're all set!" : nextStep?.title}
                  </Text>

                  <Text
                    as="span"
                    variant="caption"
                    color="secondary"
                    className="line-clamp-2 leading-normal"
                  >
                    {allComplete
                      ? 'Your organisation is ready to go.'
                      : nextStep
                        ? FAB_DESCRIPTIONS[nextStep.key]
                        : undefined}
                  </Text>
                </div>
              )}
            </div>

            {/* Right: progress ring */}
            <div className="shrink-0 pt-1">
              <ProgressRing
                completedSteps={completedSteps}
                totalSteps={totalSteps}
                allComplete={allComplete}
                accentColor={accentColor}
                isPulsing={isPulsing}
                size={48}
              />
            </div>
          </div>

          {/* Bottom action row — always rendered for stable height, visibility toggled */}
          <div
            className={`mt-3 flex justify-end border-t border-border-default pt-3 transition-opacity duration-200 ${
              showingSuccess ? 'pointer-events-none opacity-0' : 'opacity-100'
            }`}
          >
            <Button
              variant={allComplete ? 'success' : 'neutral'}
              size="sm"
              className="relative overflow-hidden rounded-[10px] px-4 py-1.5 text-xs font-bold"
              style={{ background: accentColor }}
              onClick={handleCtaClick}
              data-testid="getting-started-fab-cta"
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 animate-[gsfab-shimmer_3s_ease-in-out_infinite]"
                style={{
                  background:
                    'linear-gradient(90deg, transparent 0%, rgb(255 255 255 / 0.1) 50%, transparent 100%)',
                  backgroundSize: '200% 100%',
                }}
              />
              <span className="flex items-center gap-1 text-on-primary">
                {allComplete
                  ? 'Go to my sites'
                  : (nextStep?.ctaLabel ?? 'Continue')}
                {allComplete ? (
                  <CheckCircleOutlineIcon
                    aria-hidden="true"
                    className="size-3.5"
                  />
                ) : (
                  <ArrowForwardIcon aria-hidden="true" className="size-3.5" />
                )}
              </span>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
